import React from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {useGetTenantLeadDetailsQuery} from 'src/containers/LeadCompanyClients/api';
import {EU_COUNTRIES, TLeadClient} from 'src/containers/LeadCompanyClients/definitions';
import {useGetResellersQuery} from 'src/containers/Resellers';
import {useGetAccountManagersQuery} from 'src/containers/ResponsibleManager';
import {ERoles, useGetCurrentUserQuery} from 'src/containers/User';
import {getCountryLabel} from 'src/utils';

import {EResidentCountry, TCompanyDetails} from './definitions';
import * as S from './style';
import {getResidentCountry} from './utils';

type TExactDetailsProps = {
  detailsLabel: string;
  detailsValue: ValueOf<TCompanyDetails> | ValueOf<TLeadClient>;
};

const ExactDetails = React.memo<TExactDetailsProps>(({detailsLabel, detailsValue}) => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  const {data: user} = useGetCurrentUserQuery();

  const canReadManagersInfo =
    !!user?.roles.includes(ERoles.ROLE_ACCOUNT_MANAGER) ||
    !!user?.roles.includes(ERoles.ROLE_SUPPORT);

  const {data: tenantLeadDetails} = useGetTenantLeadDetailsQuery(supportTenantId);
  const {data: resellers = []} = useGetResellersQuery(undefined, {skip: !canReadManagersInfo});
  const {data: accountManagers = []} = useGetAccountManagersQuery(undefined, {
    skip: !canReadManagersInfo,
  });

  const getField = () => {
    switch (detailsLabel) {
      case 'resellerId':
        return (
          resellers?.find((item) => item.id === detailsValue)?.name ??
          t('LeadCompanyClients:leadClientCard.empty')
        );
      case 'documentType':
        return t(`CompanyDetails:documentTypes.${detailsValue}`);
      case 'activateLicenseDebt':
        return t(`CompanyDetails:activateLicenseDebtOptions.${detailsValue}`);
      case 'paymentType':
        return t(`CompanyDetails:paymentTypes.${detailsValue}`);
      case 'securityDeposit':
      case 'maxOverdraft':
        return (
          <S.Price
            currency={tenantLeadDetails?.currencyCode}
            value={Math.abs(Number(detailsValue) ?? 0)}
            size="S"
          />
        );
      case 'termBeforeTerminationLicense':
      case 'licensePaymentTerm':
        return `${detailsValue} ${t('Common:time.days', {count: Number(detailsValue)})}`;
      case 'country':
        return getCountryLabel(t, detailsValue as string);
      case 'managerResponsibleForCheckingId':
        return (
          accountManagers?.find((item) => item.id === detailsValue)?.name ??
          t('LeadCompanyClients:leadClientCard.empty')
        );
      default:
        return detailsValue;
    }
  };

  return <>{getField()}</>;
});

ExactDetails.displayName = 'ExactDetails';

type TDetailsProps = {
  details: Partial<TCompanyDetails | TLeadClient>;
  countryCode?: TLeadClient['countryCode'];
};

const Content = React.memo<TDetailsProps>(({details, countryCode}) => {
  const {t} = useTranslation();

  const residentCountry = getResidentCountry(countryCode);

  const getLabel = (key: string) => {
    if (key === 'taxNumber') {
      if (residentCountry === EResidentCountry.BLR) {
        return t(`CompanyDetails:blrTaxNumber`);
      }

      if (residentCountry === EResidentCountry.KAZ) {
        return t(`CompanyDetails:binNumber`);
      }

      if (countryCode && EU_COUNTRIES.includes(countryCode)) {
        return t(`CompanyDetails:vatNumber`);
      }
    }

    return t(`CompanyDetails:${key}`);
  };

  return (
    <>
      {Object.entries(details).map(([key, value]) => {
        if (value !== null && value !== undefined) {
          return (
            <S.CenterRow key={key}>
              <S.DetailsLabel>{getLabel(key)}</S.DetailsLabel>
              <ExactDetails detailsLabel={key} detailsValue={value} />
            </S.CenterRow>
          );
        }
      })}
    </>
  );
});

Content.displayName = 'Content';

export default Content;
